import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges, OnInit,
  Output,
  PLATFORM_ID,
  signal,
  computed,
  Signal,
  WritableSignal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {TranslationService} from "../../../core/services/translation.service";
import {Offer} from "../../../data/models/offer.model";
import {ConfigService} from "../../../core/services/config.service";
import {Bonus} from "../../../data/models/bonus.model";
import {OfferPrice} from "../../../data/models/price.model";
import {BaseData} from "../../../data/models/base-data.model";
import {MatStepper} from "@angular/material/stepper";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {PaymentProviderService} from "../../../core/services/payment-provider.service";
import {CurrencyPipe, DatePipe, DecimalPipe, isPlatformBrowser, NgClass, NgOptimizedImage} from '@angular/common';
import {OrderResultModel} from "../../../data/models/orderResult.model";
import {MatCard, MatCardContent, MatCardImage} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {HouseNoPipe} from "../../../core/pipes/houseno.pipe";
import {MatCheckbox} from "@angular/material/checkbox";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatError} from "@angular/material/form-field";
import {ImageService} from '../../../core/services/image.service';
import {OrderProcessingService} from "../../../data/services/order-processing.service";
import {DisclaimerMode} from "../../../core/models/disclaimer-mode.enum";
import {isDigital} from "../../../core/utils/offer-utils";
import {
  bonusAddressTranslationKey,
  configFlag,
  CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS,
  CONFIG_SHOW_DELIVERY_ADDRESS,
  showDeliveryAddressBlock
} from "../../../core/utils/digital-order-address";
import {AddressFormValue} from "../../../data/models/subscription-order.model";
import {BaseDataService} from "../../../core/services/base-data.service";
import {titleDescription} from "../../../core/utils/address-utils";

/** Zeigt die Abo-Details der Karte auch im letzten Bestellschritt (Zusammenfassung) an. */
export const CONFIG_SUMMARY_STEP_SHOW_SUBSCRIPTION_DETAILS = 'summaryStep.offerCard.showSubscriptionDetails';

/** Fragt bei digitalen Angeboten den Verzicht auf das Widerrufsrecht als Pflicht-Checkbox ab. Fehlt der Schlüssel, bleibt die Abfrage aktiv. */
export const CONFIG_DIGITAL_ORDER_REVOCATION_WAIVER = 'digitalOrder.revocationWaiver';

/**
 * Die Anschrift-Felder, die die Bestellübersicht ausgibt. Zwei Schreibweisen für die Hausnummer:
 * `houseNo` liefert das Adress-Formular, `houseno` das Backend-Modell.
 */
type AddressSummaryFields = {
  street?: string;
  houseno?: string;
  houseNo?: string;
  zipcode?: string;
  city?: string;
};

@Component({
  selector: 'app-offer-card',
  templateUrl: './offer-card.component.html',
  styleUrls: ['./offer-card.component.css'],
  imports: [
    NgClass,
    MatCard,
    MatCardImage,
    MatCardContent,
    CurrencyPipe,
    MatButton,
    HouseNoPipe,
    DatePipe,
    DecimalPipe,
    MatCheckbox,
    FormsModule,
    MatError,
    ReactiveFormsModule,
    NgOptimizedImage
  ],
  providers: [
    DatePipe
  ],
  standalone: true
})
export class OfferCardComponent implements OnInit, OnChanges {
  private navigationService = inject(NavigationService);
  translationService = inject(TranslationService);
  configService = inject(ConfigService);
  sanitizer = inject(DomSanitizer);
  private paymentProvider = inject(PaymentProviderService);
  private platformId = inject<Object>(PLATFORM_ID);
  public imageService = inject(ImageService);
  private orderProcessService = inject(OrderProcessingService);
  private datePipe = inject(DatePipe);
  private baseDataService = inject(BaseDataService);

  @Input() offer: Offer;
  @Input() showOnOfferPage: boolean = true;
  @Input() bonuses: Bonus[];
  @Input() deliveryAddress: any;
  @Input() billingAddress: any;
  @Input() bonusAddress: any;
  @Input() advertiserAddress: any;
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() prices: OfferPrice[];
  @Input() paymentCode: any;
  @Input() paymentFrequency: BaseData;
  @Input() stepper: MatStepper;
  creatingOrder = input<boolean>(false);
  onlinePayment = input<boolean>(false);
  @Input() disclaimerMode: DisclaimerMode = DisclaimerMode.SEPARATE_CHECKBOXES;
  @Input() showOnlyOptIns: boolean = false;
  isCalculatingPrice = input<boolean>(false);
  @Output() save: EventEmitter<OrderResultModel> = new EventEmitter<OrderResultModel>();
  @Output() optInsSelected: EventEmitter<Map<number,  boolean>> = new EventEmitter<Map<number,  boolean>>();

  @Input() optionalBillingAddressValid: boolean = false;

  highlightIds: string[];
  @ViewChild('saveButtonPoint') saveButtonPoint: ElementRef<HTMLElement>;
  @ViewChild('offerCard') offerCard: ElementRef<HTMLElement>;
  orderResult: OrderResultModel = {agb: false, privacy: false, disclaimer: false, paymentTransaction: null};

  agb: boolean = false;
  privacy:boolean = false;
  disclaimer: boolean = false;
  privacyDisclaimerAgb:boolean = false;
  epaperRevocationWaiver: boolean = false;
  checkBoxes: boolean = false;
  agreementTouched = {
    agb: false,
    disclaimer: false,
    privacy: false,
    privacyDisclaimerAgb: false,
    epaperRevocationWaiver: false
  };

  optinResult: Map<number, boolean>;
  optinTouched: Set<number> = new Set<number>();

  //Für den Zugriff auf die Modus-Konstanten im Template
  protected readonly DisclaimerMode = DisclaimerMode;

  additionalAgbInfo1: string;
  additionalAgbInfo2: string;
  showPriceSegment: boolean = true;
  summarySafeHtml: SafeHtml = '';
  onlinePaymentFinished: WritableSignal<boolean> = signal(false);
  showOrderButton: Signal<boolean> = computed(() =>
    (!this.onlinePayment() && !this.isCalculatingPrice()) || (this.onlinePaymentFinished() && !this.isCalculatingPrice())
  );

  constructor() {
    const offerIds = this.configService.getConfig('offer.highlight.offerId');
    if (offerIds?.value !== '') {
      this.highlightIds = offerIds?.value?.split(',');
    }

    this.showPriceSegment = this.configService.getConfig('offer.card.showPriceSegment')?.asBoolean() ?? true;

    //Auf die Änderungen von Online-Bezahlung hören
    //
    //Ob die Zahlung durch ist, entscheidet der Zahlungs-Schritt (state.onlinePaymentFinished) - dem
    //meldet der Anbieter den Erfolg. Hier wurde das früher ein zweites Mal aus den Feldern der
    //Transaktion abgeleitet, und zwar nach einer anderen Regel als im onlinepayment-Component: Eine
    //Zahlung mit billingPlanId, die als einmalige Order autorisiert wurde, galt weder als Order
    //(wegen des Plans) noch als Subscription (weil die subscriptionId fehlt). Ergebnis: bezahlt,
    //aber kein Bestell-Button.
    this.paymentProvider.getPaymentProviderResultData().subscribe(transaction => {
      if (transaction) {
        this.orderResult.paymentTransaction = transaction;
      }
    });

    this.optinResult = new Map<number, boolean> ();

    this.additionalAgbInfo1 = this.translationService.getTranslation("offer.additionalInfo.beforeAgb");
    this.additionalAgbInfo2 = this.translationService.getTranslation("offer.additionalInfo.afterAgb");
  }

  ngOnInit(): void {
     if (this.showOnOfferPage) {
       return;
     }

     this.orderProcessService.getOrderState().subscribe(state => {
       this.onlinePaymentFinished.set(!!state.onlinePaymentFinished);

       if (state.offer) {
         this.offer = state.offer;
       }

       if (state.addresses?.billingAddress) {
         this.billingAddress = state.addresses?.billingAddress;
       }

       if (state.addresses?.deliveryAddress) {
         this.deliveryAddress = state.addresses?.deliveryAddress;
       }

       // Beide Adressen werden gemeinsam uebernommen, sobald es ueberhaupt Praemiendaten gibt:
       // Die Praemie geht entweder an den Besteller oder an den Werber, nie an beide. Wuerde nur
       // die jeweils gefuellte Adresse gesetzt, bliebe nach einem Wechsel der Praemie die alte
       // stehen und die Uebersicht zeigte zwei Empfaenger.
       if (state.bonusData) {
         this.bonusAddress = state.bonusData.bonusAddress;
         this.advertiserAddress = state.bonusData.advertiserAddress;
       }

       if (state.payment) {
         if (state.payment.formOfPayment) {
           this.paymentCode = state.payment?.formOfPayment;
         }

         if (state.payment.paymentFrequency) {
           this.paymentFrequency = state.payment.paymentFrequency;
         }

         if (state.payment?.startdate) {
           this.startDate = new Date(state.payment?.startdate);
         }

         if (state.offerPrice) {
           this.prices = state.offerPrice;
         }
       }
     })
  }


  isDigitalOffer(): boolean {
    return isDigital(this.offer?.offerType);
  }

  checkBoxesChecked(): boolean {
    // AGB/Widerruf/Datenschutz – einzeln (getrennt), als Sammel-Checkbox (kombiniert) oder ganz ohne
    // Checkbox: dann gilt der Erklärungstext über dem Bestell-Button mit der Buchung als angenommen.
    let baseChecked: boolean;
    switch (this.disclaimerMode) {
      case DisclaimerMode.TEXT_WITHOUT_CHECKBOX:
        baseChecked = true;
        break;
      case DisclaimerMode.ONE_CHECKBOX:
        baseChecked = this.privacyDisclaimerAgb;
        break;
      default:
        baseChecked = this.agb && this.privacy && this.disclaimer;
        break;
    }
    // Bei digitalen Angeboten (E-Paper/PaidContent) zusätzlich der Widerrufsverzicht – als eigene Checkbox in allen Opt-In-Modi,
    // sofern per Konfiguration nicht abgeschaltet.
    return baseChecked && (!this.askForRevocationWaiver() || this.epaperRevocationWaiver);
  }

  /**
   * Wird der Verzicht auf das Widerrufsrecht abgefragt? Nur bei digitalen Angeboten und nur, solange
   * {@link CONFIG_DIGITAL_ORDER_REVOCATION_WAIVER} ihn nicht abschaltet. Wie {@link showDeliveryAddress}
   * bei jedem Aufruf gelesen, damit ein später geladener Konfigurationswert noch greift.
   */
  askForRevocationWaiver(): boolean {
    return this.isDigitalOffer()
      && configFlag(this.configService.getConfig(CONFIG_DIGITAL_ORDER_REVOCATION_WAIVER), true);
  }

  isOrderButtonDisabled(): boolean {
    return this.creatingOrder()
      || !this.isOptinValid()
      || !this.checkBoxesChecked()
      || !this.optionalBillingAddressValid;
  }

  openOrder(id: number): void{
    this.navigationService.navigateTo(NavigationCommand.OFFER, new Array(id+'')).then(() => {});
  }

  //TODO: Hier muss geprüft werden, ob eine Transaction mit CAPTURE da ist
  saveOrder(): void {
    this.checkBoxes = true;
    if (this.checkBoxesChecked()) {
      this.orderResult.agb = true;
      this.orderResult.privacy = true;
      this.orderResult.disclaimer = true;
      this.save.emit(this.orderResult);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.offer) {
      this.summarySafeHtml = this.sanitizer.bypassSecurityTrustHtml(this.offer?.summary ?? '');
    }

    if (isPlatformBrowser(this.platformId) && changes.prices && !changes.prices.firstChange) {
      const elementPosition = this.saveButtonPoint?.nativeElement?.getBoundingClientRect()?.top;
      const cardPosition = this.offerCard?.nativeElement?.getBoundingClientRect();
      const absoluteElementTop = cardPosition?.top + window.scrollY;
      const middle = absoluteElementTop - (cardPosition?.height / 2);

      if ( elementPosition && elementPosition > 0) {
        window.scrollBy({left: 0, top: middle, behavior: 'smooth'});
      }
    }
  }

  onOptInClick(event, optin): void {
    this.optinTouched.add(optin.id);
    this.optinResult.set(optin.id, event.checked);
    this.optInsSelected.emit(this.optinResult);

    //Ausgewählte OptIns werden an den Service geschickt
    this.orderProcessService.update(state => ({
      ...state,
      optinList: this.optinResult
    }))
  }

  isOptinValid(): boolean {
    if (!this.offer?.optIns?.length) {
      return true;
    }
    return this.offer.optIns.every(e => !e.mandatory || !!this.optinResult.get(e.id));
  }

  changeAgb() {
    if (this.disclaimerMode === DisclaimerMode.TEXT_WITHOUT_CHECKBOX) {
      //Ohne Checkbox wird keine Kenntnisnahme erfasst - es wird also auch keine an den Controller übermittelt.
      return;
    }

    const checkedAgb = this.checkBoxesChecked();
    this.orderProcessService.update(state => ({
      ...state,
      userAgreement: {
        ...state.userAgreement,
        checkAGB: checkedAgb,
        checkPrivacy: checkedAgb,
        checkSchufa: checkedAgb,
        checkWiderruf: checkedAgb,
      }
    }));
  }

  onAgreementChanged(type: 'agb' | 'disclaimer' | 'privacy' | 'privacyDisclaimerAgb' | 'epaperRevocationWaiver'): void {
    this.agreementTouched[type] = true;
    this.changeAgb();
  }

  shouldShowAgreementError(type: 'agb' | 'disclaimer' | 'privacy' | 'epaperRevocationWaiver'): boolean {
    const value = this[type];
    return this.agreementTouched[type] && !value;
  }

  shouldShowPrivacyDisclaimerAgbError(): boolean {
    return this.agreementTouched.privacyDisclaimerAgb && !this.privacyDisclaimerAgb;
  }

  shouldShowOptinError(optinId: number): boolean {
    return this.optinTouched.has(optinId) && !this.optinResult.get(optinId);
  }

  isString(x): boolean {
    return Object.prototype.toString.call(x) === '[object String]';
  }

  /** Der Titel der Person für die Anzeige, s. {@link titleDescription}. */
  titleText(title: string | BaseData): string {
    return titleDescription(title, this.baseDataService.getBaseData('titles'));
  }

  isOfferHighlighted(): boolean {
    const orderChoiceKey = this.offer?.orderChoiceKey;
    if (orderChoiceKey === null || orderChoiceKey === undefined || !this.highlightIds?.length) {
      return false;
    }
    return this.highlightIds.includes(String(orderChoiceKey));
  }

  /**
   * Im letzten Bestellschritt stehen die Abo-Details bereits in der Zusammenfassung selbst - die Karte
   * blendet sie dort deshalb aus, es sei denn {@link CONFIG_SUMMARY_STEP_SHOW_SUBSCRIPTION_DETAILS}
   * ist aktiv (Default: aus). Die Konfiguration wird wie bei den Adressen bei jedem Aufruf gelesen.
   */
  showOrderSummaryDetails(): boolean {
    if (!this.stepper || this.stepper.selectedIndex < (this.stepper.steps.length - 1)) {
      return true;
    }
    return configFlag(this.configService.getConfig(CONFIG_SUMMARY_STEP_SHOW_SUBSCRIPTION_DETAILS), false);
  }

  /**
   * Jede Adresse wird einzeln eingeblendet, sobald ihre Anschrift vollständig ist.
   *
   * Alle vier kommen als Live-Formularwert aus dem Adress- bzw. Prämien-Schritt (OrderState). Dieses
   * Objekt existiert ab der ersten Emission — mit lauter leeren Feldern. Eine Prüfung auf das Objekt
   * allein wäre deshalb immer wahr und zeigte das Label mit leerer Anschrift daneben.
   */
  /**
   * Die Konfigurationen werden hier bei jedem Aufruf gelesen, nicht einmalig im Konstruktor: Diese
   * Karte wird auch auf Routen erzeugt, die die Schlüssel nicht vorladen, und der Bestell-Ablauf
   * baut sie auf, bevor der Resolver sie geliefert hat. Ein im Konstruktor eingefrorener Wert bliebe
   * dann für die Lebensdauer der Karte falsch.
   */
  showDeliveryAddress(): boolean {
    const configured = configFlag(this.configService.getConfig(CONFIG_SHOW_DELIVERY_ADDRESS), true);
    return showDeliveryAddressBlock(this.offer?.offerType, configured)
      && this.isAddressComplete(this.deliveryAddress);
  }

  showBillingAddress(): boolean {
    return this.isAddressComplete(this.billingAddress);
  }

  showBonusAddress(): boolean {
    return this.isAddressComplete(this.bonusAddress);
  }

  /** Titel der Prämienadress-Kachel - bei digitalen Abos ggf. der der Lieferadresse. */
  bonusAddressTitle(): string {
    return this.translationService.getTranslation(bonusAddressTranslationKey(
      this.offer?.offerType,
      configFlag(this.configService.getConfig(CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS), false)));
  }

  showAdvertiserAddress(): boolean {
    return this.isAddressComplete(this.advertiserAddress);
  }

  /**
   * Überschrift und Trennlinie des Adressblocks gehören zu keiner einzelnen Adresse — sie erscheinen,
   * sobald irgendeine Adresse angezeigt wird.
   */
  showAddressSummary(): boolean {
    return this.showDeliveryAddress()
      || this.showBillingAddress()
      || this.showBonusAddress()
      || this.showAdvertiserAddress();
  }

  /**
   * Vollständig heißt: alle vier Felder der angezeigten Anschrift sind gefüllt. Der Name wird
   * bewusst nicht geprüft — er kommt je nach Flow aus unterschiedlichen Feldern (etwa `initials`
   * statt `firstName`), eine Namensprüfung würde vollständige Adressen zu Unrecht verstecken.
   */
  private isAddressComplete(addressValue: AddressFormValue): boolean {
    const address = addressValue?.address as AddressSummaryFields;
    if (!address) {
      return false;
    }
    return this.hasContent(address.street)
      && this.hasContent(address.houseno ?? address.houseNo)
      && this.hasContent(address.zipcode)
      && this.hasContent(address.city);
  }

  private hasContent(value: string): boolean {
    return typeof value === 'string' ? value.trim().length > 0 : value != null;
  }

  /* Der initiale OrderState enthält ein leeres offerPrice-Array — erst ein Eintrag bedeutet, dass berechnet wurde */
  hasCalculatedPrices(): boolean {
    return !!this.prices?.length;
  }

  hasConfiguredPriceInfo(): boolean {
    if (this.offer?.priceCalculationActivated) {
      return this.offer?.combiPriceCalculated != null
        || this.offer?.offerPrice?.price != null
        || !!this.offer?.additionalPriceDescription;
    }
    return !!this.offer?.priceCalculationSummary;
  }
}
